import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/user';
import { AppDataContext } from 'contexts/appData';
import { formatPaymentError, groupPaymentMethods, PAYMENT_FLOW_VIEWS, PaymentGroupEnum, PaymentMethodsEnum } from './helpers';
import { HistoryContext } from 'contexts/history';
import { ModalsContext } from 'contexts/modals';
import { Constants, useFormSubmit, useToggle } from 'libs';
import { getClientId, getSessionId } from 'libs/helpers';

export const PaymentContext = createContext(null);

const PaymentProvider = ({ children, modalData }) => {
  const { paymentTypeId, price, params = {}, processPayment, callback, onPaymentMethodSelect } = modalData;

  const { api, CONFIG: config } = useContext(AppDataContext);
  const { userBalance, accountDetails } = useContext(UserContext);
  const { getPathname } = useContext(HistoryContext);
  const { callResponse } = useContext(ModalsContext);
  const { submitForm } = useFormSubmit();

  const cardSelection = useToggle();
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [merchantId, setMerchantId] = useState(null);
  const [paymentFlowView, setPaymentFlowView] = useState(PAYMENT_FLOW_VIEWS.PAYMENT_METHODS_LIST);

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  const [token, setToken] = useState(null);
  const [walletToken, setWalletToken] = useState(null);

  const [saveCard, setSaveCard] = useState(false);
  const hasTrackedPaymentMethod = useRef(false);

  const totalAmount = parseFloat(price).toFixed(2);
  const isInsufficientBalanceAmount = parseFloat(totalAmount) > parseFloat(userBalance);

  const trackPaymentMethodSelection = (paymentMethod) => {
    if (!onPaymentMethodSelect || hasTrackedPaymentMethod.current) return;
    const tracked = onPaymentMethodSelect({
      paymentMethodId: paymentMethod.id,
      hasSelectedCard: Boolean(selectedCard),
    });
    if (tracked !== false) hasTrackedPaymentMethod.current = true;
  };

  const onSuccess = () => {
    callback(null, 'success');
    callback(null, 'destroy');
  };

  const fetchToken = async ({ saveCard = false } = {}) => {
    const query = {
      PaymentMethodID: Constants.PaymentMethodsEnum.tbc_card,
      PaymentTypeID: paymentTypeId,
      Amount: totalAmount,
      ReturnURL: config.HOST.appUrl + getPathname().substring(1),
      SaveCard: saveCard,
      Embedded: true,
      ...params,
    };

    const clientId = '';
    const sessionId = '';
    // const [clientId, sessionId] = await Promise.all([getClientId(), getSessionId()]);
    return await api
      .processPay({ ...query, ...(clientId ? { client_id: clientId } : {}), ...(sessionId ? { session_id: sessionId } : {}) })
      .then((res) => {
        if (res.statusCode === 200) {
          const order = res.data?.Order;
          const token = order.PaymentData?.token;

          setToken(token);
          return token;
        } else {
          callResponse({ statusCode: false, statusMessage: formatPaymentError(res.statusMessage) });
        }
      })
      .finally(() => setIsPaymentProcessing(false));
  };

  const fetchWalletToken = async ({ saveCard = false, paymentOption } = {}) => {
    const query = {
      PaymentMethodID: paymentOption.id,
      PaymentTypeID: paymentTypeId,
      Amount: totalAmount,
      ReturnURL: config.HOST.appUrl + getPathname().substring(1),
      SaveCard: saveCard,
      Embedded: true,
      ...params,
    };

    return await api
      .processPay({ ...query })
      .then((res) => {
        if (res.statusCode === 200) {
          const order = res.data?.Order;
          const token = order.PaymentData?.token;

          setWalletToken(token);
          return token;
        } else {
          callResponse({ statusCode: false, statusMessage: formatPaymentError(res.statusMessage) });
        }
      })
  };


  const handlePayment = async (paymentMethod) => {
    setIsPaymentProcessing(true);
    trackPaymentMethodSelection(paymentMethod);
    if (processPayment) return processPayment(paymentMethod);

    const query = {
      PaymentMethodID: paymentMethod.id,
      PaymentTypeID: paymentTypeId,
      Amount: totalAmount,
      ReturnURL: config.HOST.appUrl + getPathname().substring(1),
      ...params,
    };

    if (paymentMethod.id === PaymentMethodsEnum.SavedCard) {
      query['UserCardID'] = selectedCard.uuid;
      query['PayWith'] = 'charge';
    }

    query['SaveCard'] = saveCard;

    const clientId = '';
    const sessionId = '';
    // const [clientId, sessionId] = await Promise.all([getClientId(), getSessionId()]);
    api
      .processPay({ ...query, ...(clientId ? { client_id: clientId } : {}), ...(sessionId ? { session_id: sessionId } : {}) })
      .then((res) => {
        if (res.statusCode === 200) {

          const order = res.data?.Order;

          if (!order?.PaymentURL && !order?.PaymentData?.token) return onSuccess();

          if (order?.PaymentData.token) {
            setToken(order.PaymentData.token);
            setPaymentFlowView(PAYMENT_FLOW_VIEWS.CARD_FILL);
            return;
          }

          submitForm({ action: order.PaymentURL, method: order.PaymentMethod || 'GET', fields: order.PaymentData });
        } else {
          callResponse({ statusCode: false, statusMessage: formatPaymentError(res.statusMessage) });
        }
      })
      .finally(() => setIsPaymentProcessing(false));
  };

  useEffect(() => {
    Promise.all([
      api.getPaymentGroups({ paymentTypeId, amount: totalAmount }),
      api.getPaymentMethods({ paymentTypeId, amount: totalAmount }),
      api.getUserCards(),
      api.getMerchantId({ paymentTypeId }),
      accountDetails(),
    ])
      .then(([paymentGroupsRes, paymentMethodsRes, cardsRes, merchantIdRes]) => {
        const paymentGroups = paymentGroupsRes.data ?? [];
        const paymentMethods = paymentMethodsRes.data ?? [];
        const merchantId = merchantIdRes.data?.merchant_id;
        const cards = cardsRes?.data?.UserCards ?? [];
        const paymentOptions = groupPaymentMethods(paymentGroups, paymentMethods);


        if(cards.length > 0) {
          const cardPaymentMethodIndex = paymentOptions.findIndex((item) => item.group_id === PaymentGroupEnum.Cards);
          if (cardPaymentMethodIndex !== -1) paymentOptions.splice(cardPaymentMethodIndex, 0, { name: '', id: PaymentMethodsEnum.SavedCard, group_id: 0 });
        }

        setMerchantId(merchantId);
        setPaymentOptions(paymentOptions);
        setCards(cards);
        setSelectedCard(cards.find((card) => card.favorite === '1') || cards[0]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <PaymentContext.Provider
      value={{
        isLoading,
        paymentOptions,
        setSelectedPaymentMethod,
        selectedPaymentMethod,
        cards,
        selectedCard,
        setSelectedCard,
        totalAmount,
        isInsufficientBalanceAmount,
        merchantId,
        paymentFlowView,
        setPaymentFlowView,
        isPaymentProcessing,
        setIsPaymentProcessing,
        fetchToken,
        token,
        handlePayment,
        onSuccess,
        cardSelection,
        saveCard,
        setSaveCard,
        setWalletToken,
        walletToken,
        fetchWalletToken,
        trackPaymentMethodSelection

      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePaymentContext must be used within a PaymentContext');
  }
  return context;
};

export default PaymentProvider;
